import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircleCheckIcon, Loader2, OctagonXIcon, XIcon } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { toastError, presentMutationError } from "../../utils/showApiErrorToast";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { TradeFilterSelectFullWidth } from "../trade-filter-selects";
import { useReportStrategyPineRequirements } from "../../hooks/useReportStrategyPineRequirements";
import {
  combineReportStrategyPolicies,
  previewCombineReportStrategyPolicies,
} from "../../services/reportsApi";
import type { ReportStrategyPolicyVersion } from "../../types/reports";
import type { CombinePoliciesPreviewResponse } from "../../types/reportStrategyPine";
import {
  countPolicyVersionSegments,
  formatCombinePolicyError,
  versionOptionLabel,
} from "../../utils/reportStrategyPineUi";
import { combineStackVisibilityFromPolicyVersions } from "../../utils/combineStackVisibility";
import { STRATEGY_PINE_SECTION_DESC_CLASS, STRATEGY_PINE_SECTION_LABEL_CLASS } from "./StrategyPineSectionCard";
import { StrategyPineParseWarnings } from "./StrategyPineParseWarnings";

export type StrategyCombinePoliciesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  strategyId: string;
  versions: ReportStrategyPolicyVersion[];
  selectedVersionIds: string[];
  onCombined: (policyVersionId: string) => void;
  onVersionsRefresh: () => void | Promise<void>;
};

export function StrategyCombinePoliciesDialog({
  open,
  onOpenChange,
  strategyId,
  versions,
  selectedVersionIds,
  onCombined,
  onVersionsRefresh,
}: StrategyCombinePoliciesDialogProps): React.ReactElement {
  const { data: requirements } = useReportStrategyPineRequirements();
  const [bundleTitle, setBundleTitle] = useState("");
  const [entryKeepCombine, setEntryKeepCombine] = useState<"any" | "all">("all");
  const [entryVetoCombine, setEntryVetoCombine] = useState<"any" | "all">("any");
  const [previewing, setPreviewing] = useState(false);
  const [combining, setCombining] = useState(false);
  const [preview, setPreview] = useState<CombinePoliciesPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const selectedVersions = useMemo(
    () => versions.filter((v) => selectedVersionIds.includes(v.id)),
    [selectedVersionIds, versions]
  );
  const selectedEmptySegmentVersions = useMemo(
    () => selectedVersions.filter((v) => countPolicyVersionSegments(v) === 0),
    [selectedVersions]
  );
  const selectedPoliciesSummary = useMemo(
    () =>
      selectedVersions
        .map((v) => {
          const segmentCount = countPolicyVersionSegments(v);
          const emptySegments = segmentCount === 0;
          return `${versionOptionLabel(v)}${emptySegments ? " (no segments)" : ""}`;
        })
        .join(", "),
    [selectedVersions]
  );

  const stackVisibility = useMemo(
    () => combineStackVisibilityFromPolicyVersions(selectedVersions),
    [selectedVersions]
  );
  const showKeepCombine = stackVisibility.showKeepCombine;
  const showVetoCombine = stackVisibility.showVetoCombine;

  const previewErrorsRaw = preview?.errors;
  const previewErrors = Array.isArray(previewErrorsRaw) ? previewErrorsRaw : [];
  const isPreviewInvalid = Boolean(
    preview && (preview.ok === false || preview.valid === false || previewErrors.length > 0)
  );
  const previewWarningsRaw = preview?.warnings;
  const previewWarnings = Array.isArray(previewWarningsRaw) ? previewWarningsRaw : [];
  const previewReady = Boolean(preview && !isPreviewInvalid && !previewError);
  const resolvedCandleTf =
    typeof preview?.resolved_candle_timeframe === "string"
      ? preview.resolved_candle_timeframe.trim()
      : "";

  useEffect(() => {
    if (!open) return;
    setBundleTitle("");
    setEntryKeepCombine("all");
    setEntryVetoCombine("any");
    setPreview(null);
    setPreviewError(null);
  }, [open, selectedVersionIds]);

  const onPreview = useCallback(async () => {
    if (selectedVersionIds.length < 2) {
      toast.warning("Select at least two policy versions to combine");
      return;
    }
    if (selectedEmptySegmentVersions.length > 0) {
      const names = selectedEmptySegmentVersions.map((v) => versionOptionLabel(v)).join(", ");
      setPreview(null);
      setPreviewError(
        `Selected versions contain no policy segments: ${names}. Re-save these versions from a report run before combining.`
      );
      toast.warning("Selected versions contain no policy segments");
      return;
    }
    setPreviewing(true);
    setPreviewError(null);
    try {
      const resp = await previewCombineReportStrategyPolicies(strategyId, {
        policy_version_ids: selectedVersionIds,
        ...(showKeepCombine ? { entry_keep_combine: entryKeepCombine } : {}),
        ...(showVetoCombine ? { entry_veto_combine: entryVetoCombine } : {}),
      });
      setPreview(resp);
      const errors = Array.isArray(resp.errors) ? resp.errors : [];
      const invalid = resp.ok === false || resp.valid === false || errors.length > 0;
      if (invalid && errors.length > 0) {
        const first = errors[0];
        setPreviewError(formatCombinePolicyError(first?.code, first?.message, requirements));
      } else if (invalid) {
        setPreviewError("Preview returned validation errors. Please adjust selected policy versions.");
      }
    } catch (e) {
      setPreview(null);
      setPreviewError(e instanceof Error ? e.message : "Preview failed.");
    } finally {
      setPreviewing(false);
    }
  }, [
    entryKeepCombine,
    entryVetoCombine,
    requirements,
    selectedEmptySegmentVersions,
    selectedVersionIds,
    showKeepCombine,
    showVetoCombine,
    strategyId,
  ]);

  const onCombine = useCallback(async () => {
    if (selectedVersionIds.length < 2) {
      toast.warning("Select at least two policy versions");
      return;
    }
    if (selectedEmptySegmentVersions.length > 0) {
      toast.warning("Selected versions contain no policy segments");
      return;
    }
    if (!previewReady) {
      toast.warning("Preview combine before saving");
      return;
    }
    setCombining(true);
    try {
      const resp = await combineReportStrategyPolicies(strategyId, {
        policy_version_ids: selectedVersionIds,
        ...(bundleTitle.trim() ? { title: bundleTitle.trim() } : {}),
        ...(showKeepCombine ? { entry_keep_combine: entryKeepCombine } : {}),
        ...(showVetoCombine ? { entry_veto_combine: entryVetoCombine } : {}),
      });
      const newId =
        typeof resp.policy_version_id === "string"
          ? resp.policy_version_id
          : (resp.policy_version as { id?: string } | undefined)?.id;
      if (newId) {
        onCombined(newId);
      }
      if (resp.compose_recommended) {
        toast.success("Bundle saved. Compose to TradingView Pinescript to use this policy.");
      } else if (newId) {
        toast.success("Combined policy bundle saved");
      } else {
        toast.success("Combined policy bundle saved");
      }
      onOpenChange(false);
      await onVersionsRefresh();
    } catch (e) {
      presentMutationError(e, "Combine failed");
    } finally {
      setCombining(false);
    }
  }, [
    bundleTitle,
    entryKeepCombine,
    entryVetoCombine,
    onCombined,
    onOpenChange,
    onVersionsRefresh,
    previewReady,
    selectedEmptySegmentVersions.length,
    selectedVersionIds,
    showKeepCombine,
    showVetoCombine,
    strategyId,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        data-strategy-combine-policies-dialog=""
        className="sm:max-w-lg"
      >
        <DialogClose asChild>
          <button
            type="button"
            data-strategy-combine-policies-dismiss=""
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex size-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>

        <DialogHeader>
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            Combine policies
          </DialogTitle>
          <DialogDescription className="font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-white">
            Merge selected policy versions into one combined policy. Test before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field data-strategy-combine-policies-selected="">
            <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Selected policies</FieldLabel>
            <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
              {selectedPoliciesSummary}
            </FieldDescription>
          </Field>

          {stackVisibility.managementOnly && selectedVersions.length >= 2 ? (
            <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
              Exit Placement only — rules merge as management segments. Keep/Remove stacks are for
              Regime entry policies. Preview will flag conflicting stop/target overrides.
            </FieldDescription>
          ) : null}

          {showKeepCombine ? (
            <Field data-strategy-combine-keep-operand="">
              <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Keep stack</FieldLabel>
              <TradeFilterSelectFullWidth
                value={entryKeepCombine}
                onValueChange={(v) => {
                  setEntryKeepCombine(v as "any" | "all");
                  setPreview(null);
                  setPreviewError(null);
                }}
                options={[
                  { value: "all", label: "All keeps must match (AND)" },
                  { value: "any", label: "Any keep may match (OR)" },
                ]}
              />
              <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                How multiple keep-only segments combine.
              </FieldDescription>
            </Field>
          ) : null}

          {showVetoCombine ? (
            <Field data-strategy-combine-veto-operand="">
              <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Remove stack</FieldLabel>
              <TradeFilterSelectFullWidth
                value={entryVetoCombine}
                onValueChange={(v) => {
                  setEntryVetoCombine(v as "any" | "all");
                  setPreview(null);
                  setPreviewError(null);
                }}
                options={[
                  { value: "any", label: "Remove if any remove rule triggers (default)" },
                  { value: "all", label: "Remove only if all remove rules trigger" },
                ]}
              />
              <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                Independent of Keep stack. Applies only to remove segments in the bundle.
              </FieldDescription>
            </Field>
          ) : null}

          <Field data-strategy-combine-bundle-title-field="">
            <FieldLabel
              htmlFor="strategy-combine-bundle-title"
              className={STRATEGY_PINE_SECTION_LABEL_CLASS}
            >
              Combined policy title (optional)
            </FieldLabel>
            <Input
              id="strategy-combine-bundle-title"
              value={bundleTitle}
              onChange={(e) => setBundleTitle(e.target.value)}
              placeholder="e.g. Entry + management bundle"
              className="font-[family-name:var(--font-primary)] focus-visible:ring-inset"
            />
          </Field>

          {previewReady && resolvedCandleTf ? (
            <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
              Candle timeframe for budget/OHLC:{" "}
              <span className="text-[color:var(--brand-tropical-surf)]">{resolvedCandleTf}</span>{" "}
              (finest of sources).
            </FieldDescription>
          ) : null}

          {previewError ? (
            <Alert
              variant="destructive"
              className="rounded-lg font-[family-name:var(--font-primary)]"
            >
              <OctagonXIcon aria-hidden />
              <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                {previewError}
              </AlertDescription>
            </Alert>
          ) : null}

          {previewErrors.length > 0 ? (
            <div className="space-y-2">
              {previewErrors.map((err, i) => (
                <Alert
                  key={i}
                  variant="destructive"
                  className="rounded-lg font-[family-name:var(--font-primary)]"
                >
                  <OctagonXIcon aria-hidden />
                  <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                    {err.code ? (
                      <span className="mr-2 font-mono text-xs">{err.code}</span>
                    ) : null}
                    {err.code ? " - " : ""}
                    {formatCombinePolicyError(err.code, err.message, requirements)}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          ) : null}

          {previewWarnings.length > 0 ? (
            <StrategyPineParseWarnings warnings={previewWarnings} subtleText="gray.500" />
          ) : null}

          {previewReady ? (
            <Alert
              variant="success"
              className="rounded-lg font-[family-name:var(--font-primary)]"
            >
              <CircleCheckIcon aria-hidden />
              <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                Combine preview looks valid. Save the bundle to use it in TradingView export.
              </AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-end">
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-3"
            disabled={selectedVersionIds.length < 2 || previewing || combining}
            onClick={() => void (previewReady ? onCombine() : onPreview())}
          >
            {previewing ? (
              <>
                <Loader2 data-icon="inline-start" className="size-3.5 shrink-0 animate-spin" aria-hidden />
                <span className="normal-case tracking-normal">Testing…</span>
              </>
            ) : combining ? (
              <>
                <Loader2 data-icon="inline-start" className="size-3.5 shrink-0 animate-spin" aria-hidden />
                <span className="normal-case tracking-normal">Saving…</span>
              </>
            ) : (
              <span className="normal-case tracking-normal">
                {previewReady ? "Save Combined Policy" : "Test Combine"}
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
